import { useEffect, useRef, useCallback } from "react";
import { fetchAuthed } from "@/utils/api";
import type { GameStatusResponse, GameStatus } from "@/types/api";

const POLL_INTERVAL = 7000;
const MAX_CONSECUTIVE_ERRORS = 5;
const TERMINAL_STATUSES: GameStatus[] = ["done", "purged"];

interface UseGamePollingOptions {
  gameId: string;
  enabled: boolean;
  onUpdate: (status: GameStatusResponse) => void;
}

export function useGamePolling({
  gameId,
  enabled,
  onUpdate,
}: UseGamePollingOptions) {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const poll = useCallback(async () => {
    const data = await fetchAuthed<GameStatusResponse>(
      gameId,
      `/games/${gameId}/status`,
    );
    onUpdateRef.current(data);
    return data.status;
  }, [gameId]);

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setTimeout>;
    let stopped = false;
    let errorCount = 0;

    const run = async () => {
      try {
        const status = await poll();
        errorCount = 0;
        if (stopped) return;
        if (TERMINAL_STATUSES.includes(status)) return;
        timer = setTimeout(run, POLL_INTERVAL);
      } catch {
        if (stopped) return;
        errorCount++;
        if (errorCount >= MAX_CONSECUTIVE_ERRORS) return;
        const backoff = POLL_INTERVAL * Math.pow(2, errorCount - 1);
        timer = setTimeout(run, backoff);
      }
    };

    // Fire immediately
    run();

    // Visibility-aware: pause when hidden, refetch on focus
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && !stopped) {
        clearTimeout(timer);
        run();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, poll]);
}
